import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import { useRole } from "@hooks/useRole";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";
import SectionCard from "@features/profile/view/SectionCard";
import axios from "axios";
import { Modal as SharedModal, ModalFooter } from "@common/Modal";
import cache from "@utils/cache";

const post = (ep, f) => {
  const fd = new FormData();
  Object.entries(f).forEach(([k,v]) => { if (v != null) fd.append(k,v); });
  return axios.post(`${API_BASE}${ep}`, fd).then(r=>r.data).catch(()=>null);
};

// Convert any date format to YYYY-MM-DD for HTML date input
const toInputDate = (d) => {
  if (!d) return "";
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.split("T")[0];
  // MM-DD-YYYY → YYYY-MM-DD
  if (/^\d{2}-\d{2}-\d{4}/.test(d)) {
    const [mm, dd, yyyy] = d.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }
  // Try parsing as Date
  try {
    const dt = new Date(d);
    if (!isNaN(dt)) return dt.toISOString().split("T")[0];
  } catch {}
  return d;
};


// Parse MM-DD-YYYY or YYYY-MM-DD to Date for sorting
const parseDate = (d) => {
  if (!d) return new Date(0);
  if (/^\d{2}-\d{2}-\d{4}/.test(d)) {
    const [mm, dd, yyyy] = d.split("-");
    return new Date(`${yyyy}-${mm}-${dd}`);
  }
  return new Date(d);
};

// ── Shared modal wrapper - delegates to the app-wide shared Modal shell,
// keeping this same icon/iconBg/iconColor API so none of the 5 callers
// below need to change ──────────────────────────────────────────────────
function Modal({ title, icon, iconBg, iconColor, onClose, children, footer }) {
  const titleNode = icon ? (
    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ width:38, height:38, borderRadius:10, background:iconBg||"var(--fc-primary-light)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <i className={`fa-solid ${icon}`} style={{ fontSize:15, color:iconColor||"#2563EB" }}/>
      </div>
      <span style={{ fontSize:16, fontWeight:800, color:"var(--fc-text-primary)" }}>{title}</span>
    </div>
  ) : title;

  return (
    <SharedModal title={titleNode} onClose={onClose} maxWidth={540} footer={footer}>
      {children}
    </SharedModal>
  );
}

// ── Label style ───────────────────────────────────────────────────────────────
const LBL = { fontSize:10, fontWeight:700, color:"var(--fc-text-subtle)", textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 };
const INP = { width:"100%", boxSizing:"border-box", padding:"9px 12px", border:"1.5px solid var(--fc-border)", borderRadius:9, fontSize:13, outline:"none", fontFamily:"inherit", color:"var(--fc-text-primary)", background:"var(--fc-card-bg)" };

function inp(val, set, ph, type="text") {
  return <input type={type} value={val||""} onChange={e=>set(e.target.value)} placeholder={ph}
    style={INP} onFocus={e=>e.target.style.borderColor="#2563EB"} onBlur={e=>e.target.style.borderColor="var(--fc-border)"} />;
}

// ── Skills modal ──────────────────────────────────────────────────────────────
function SkillsModal({ skills, resourceSlug, userSlug, onClose, onRefresh }) {
  const [list,       setList]       = useState(skills || []);
  const [search,     setSearch]     = useState("");
  const [results,    setResults]    = useState([]);
  const [searching,  setSearching]  = useState(false);
  const [loadingAll, setLoadingAll] = useState(true);
  const [busy,       setBusy]       = useState({});
  const [skillFilter,setSkillFilter]= useState("");
  const timer = useRef();

  // Load ALL current skills on open (get-resource-details only returns a subset)
  useEffect(() => {
    post("get-all-raw-skills", { user_slug: userSlug, resource_slug: resourceSlug })
      .then(d => {
        const all = d?.skills || d?.skill_list || [];
        if (all.length > 0) setList(all);
        setLoadingAll(false);
      })
      .catch(() => setLoadingAll(false));
  }, []);

  const doSearch = (text) => {
    clearTimeout(timer.current);
    if (!text.trim()) { setResults([]); return; }
    setSearching(true);
    timer.current = setTimeout(() => {
      post("get-skills-by-search-text", { search_text: text, resource_slug: resourceSlug })
        .then(d => { setResults(d?.skills || d?.skill_list || []); setSearching(false); });
    }, 350);
  };

  const add = async (skill) => {
    const sid = String(skill.skill_id);
    if (list.find(s => String(s.skill_id) === sid)) return;
    setBusy(b => ({ ...b, [sid]: true }));
    const res = await post("add-new-skill", { resource_slug: resourceSlug, skill_id: skill.skill_id });
    if (res && Number(res.status) === 1) {
      setList(l => [...l, skill]);
      setResults(r => r.filter(s => String(s.skill_id) !== sid));
    }
    setBusy(b => ({ ...b, [sid]: false }));
  };

  const remove = async (skill) => {
    const sid = String(skill.skill_id);
    setBusy(b => ({ ...b, [sid]: "rm" }));
    const res = await post("remove-skill", { resource_slug: resourceSlug, skill_id: skill.skill_id });
    if (res && Number(res.status) === 1) setList(l => l.filter(s => String(s.skill_id) !== sid));
    setBusy(b => ({ ...b, [sid]: false }));
  };

  // Filter current skills by search
  const displayedList = skillFilter
    ? list.filter(s => s.skill_name.toLowerCase().includes(skillFilter.toLowerCase()))
    : list;

  return (
    <Modal title="Edit Skills" icon="fa-code" iconBg="var(--fc-primary-light)" iconColor="#2563EB"
      onClose={() => { onRefresh(); onClose(); }}
      footer={
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:12, color:"var(--fc-text-muted)" }}>{list.length} skill{list.length!==1?"s":""} total</span>
          <button onClick={() => { onRefresh(); onClose(); }} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill" style={{ minWidth:100 }}>Done</button>
        </div>
      }>

      {/* Search to add new skills */}
      <label style={LBL}>Search & Add Skills</label>
      <div style={{ position:"relative", marginBottom:results.length>0?0:16 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); doSearch(e.target.value); }}
          placeholder="Type to search and add skills..."
          style={{ ...INP, paddingRight:36 }}
          onFocus={e => e.target.style.borderColor="#2563EB"}
          onBlur={e => e.target.style.borderColor="var(--fc-border)"} />
        {searching
          ? <i className="fa-solid fa-spinner fa-spin" style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", color:"var(--fc-text-subtle)", fontSize:11 }} />
          : search && <button onClick={() => { setSearch(""); setResults([]); }}
              style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"var(--fc-text-subtle)", fontSize:12 }}>
              <i className="fa-solid fa-xmark" />
            </button>
        }
      </div>

      {/* Search results dropdown */}
      {results.length > 0 && (
        <div style={{ border:"1px solid var(--fc-border)", borderRadius:10, overflow:"hidden", maxHeight:180, overflowY:"auto", marginBottom:16, boxShadow:"0 4px 16px rgba(0,0,0,0.08)" }}>
          {results.map(s => {
            const sid = String(s.skill_id);
            const already = !!list.find(x => String(x.skill_id) === sid);
            return (
              <div key={sid}
                style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"9px 14px", borderBottom:"1px solid var(--fc-bg-light)", background:already?"var(--fc-bg-light)":"var(--fc-card-bg)", cursor:already?"default":"pointer" }}
                onMouseDown={e => { e.preventDefault(); if (!already) add(s); }}>
                <span style={{ fontSize:13, color:already?"var(--fc-text-subtle)":"var(--fc-text-secondary)", fontWeight:already?400:500 }}>{s.skill_name}</span>
                <span style={{ padding:"3px 10px", borderRadius:7, background:already?"var(--fc-border)":"var(--fc-primary-light)", color:already?"var(--fc-text-subtle)":"#2563EB", fontSize:11, fontWeight:700, pointerEvents:"none", flexShrink:0 }}>
                  {busy[sid] === true ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:9}} /> Adding</> : already ? "Added ✓" : "+ Add"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Current skills — all of them, filterable */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
        <label style={{ ...LBL, marginBottom:0 }}>Current Skills ({list.length})</label>
        {list.length > 8 && (
          <input value={skillFilter} onChange={e => setSkillFilter(e.target.value)}
            placeholder="Filter skills..." style={{ padding:"4px 10px", border:"1px solid var(--fc-border)", borderRadius:7, fontSize:12, outline:"none", fontFamily:"inherit", width:140, background:"var(--fc-card-bg)", color:"var(--fc-text-primary)" }} />
        )}
      </div>
      <div style={{ maxHeight:260, overflowY:"auto", border:"1px solid var(--fc-border-light)", borderRadius:10, padding:"8px 10px", background:"var(--fc-bg-light)" }}>
        {loadingAll && (
          <div style={{ fontSize:12, color:"var(--fc-text-subtle)", textAlign:"center", padding:"16px 0" }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ marginRight:6 }} />Loading skills...
          </div>
        )}
        {!loadingAll && displayedList.length === 0 && (
          <div style={{ fontSize:12, color:"var(--fc-text-subtle)", textAlign:"center", padding:"12px 0" }}>
            {skillFilter ? `No skills match "${skillFilter}"` : "No skills added yet. Search above to add some."}
          </div>
        )}
        {!loadingAll && <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
          {displayedList.map(s => {
            const sid = String(s.skill_id);
            return (
              <span key={sid} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px", background:"var(--fc-primary-light)", border:"1px solid var(--fc-primary-border)", borderRadius:99, fontSize:12, fontWeight:600, color:"#2563EB" }}>
                {s.skill_name}
                <button onClick={() => remove(s)} disabled={busy[sid] === "rm"}
                  style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-text-subtle)", padding:0, fontSize:10, lineHeight:1, display:"flex", alignItems:"center" }}>
                  {busy[sid] === "rm"
                    ? <i className="fa-solid fa-spinner fa-spin" style={{ fontSize:9 }} />
                    : <i className="fa-solid fa-xmark" />}
                </button>
              </span>
            );
          })}
        </div>}
      </div>
    </Modal>
  );
}

// ── Certifications modal ──────────────────────────────────────────────────────
function CertsModal({ certs, resourceSlug, userSlug, onClose, onRefresh }) {
  const [list,  setList]  = useState(certs||[]);
  const [text,  setText]  = useState("");
  const [adding,setAdding]= useState(false);
  const [busy,  setBusy]  = useState({});

  const add = async () => {
    if (!text.trim()) return;
    setAdding(true);
    const res = await post("add-certification", { resource_slug: resourceSlug, certification_title: text });
    if (res && Number(res.status)===1) {
        const newCert = res.certification || { certification_id: res.certification_id || Date.now(), certification_title: text };
        setList(l=>[...l, newCert]);
      }
    setText(""); setAdding(false);
  };

  const remove = async (c) => {
    setBusy(b=>({...b,[c.certification_id]:true}));
    await post("remove-certification", { resource_slug: resourceSlug, certification_id: c.certification_id });
    setList(l=>l.filter(x=>x.certification_id!==c.certification_id));
    setBusy(b=>({...b,[c.certification_id]:false}));
  };

  return (
    <Modal title="Edit Certifications" icon="fa-certificate" iconBg="var(--fc-amber-light)" iconColor="#D97706" onClose={()=>{onRefresh();onClose();}}
      footer={<button onClick={()=>{onRefresh();onClose();}} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill" style={{width:"100%"}}>Done</button>}>
      <label style={LBL}>Current Certifications</label>
      <div style={{ marginBottom:14 }}>
        {list.length===0 && <div style={{fontSize:12,color:"var(--fc-text-subtle)",marginBottom:8}}>No certifications yet</div>}
        {list.map(c=>(
          <div key={c.certification_id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:"var(--fc-amber-light)", border:"1px solid var(--fc-amber-border)", borderRadius:9, marginBottom:6 }}>
            <span style={{ fontSize:13, color:"var(--fc-amber-text)" }}><i className="fa-solid fa-certificate" style={{marginRight:7,color:"#F59E0B",fontSize:11}} />{c.certification_title}</span>
            <button onClick={()=>remove(c)} disabled={busy[c.certification_id]}
              style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-red)", fontSize:12, padding:"2px 6px" }}>
              {busy[c.certification_id] ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-trash" />}
            </button>
          </div>
        ))}
      </div>
      <label style={LBL}>Add New Certification</label>
      <div style={{ display:"flex", gap:8 }}>
        <input value={text} onChange={e=>setText(e.target.value)} placeholder="Certification title..."
          onKeyDown={e=>e.key==="Enter"&&add()} style={{...INP,flex:1}}
          onFocus={e=>e.target.style.borderColor="#2563EB"} onBlur={e=>e.target.style.borderColor="var(--fc-border)"} />
        <button onClick={add} disabled={adding||!text.trim()} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
          {adding?"Adding...":"+ Add"}
        </button>
      </div>
    </Modal>
  );
}

// ── Languages modal ───────────────────────────────────────────────────────────
function LangsModal({ langs, resourceSlug, userSlug, onClose, onRefresh }) {
  const [list,      setList]      = useState(langs||[]);
  const [search,    setSearch]    = useState("");
  const [results,   setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy,      setBusy]      = useState({});
  const timer = useRef();

  const doSearch = (text) => {
    clearTimeout(timer.current);
    if (!text.trim()) { setResults([]); return; }
    setSearching(true);
    timer.current = setTimeout(() => {
      post("get-languages-by-search-text", { search_text: text })
        .then(d => { setResults(d?.language_list||d?.languages||[]); setSearching(false); });
    }, 350);
  };

  const add = async (lang) => {
    const lid = lang.id || lang.language_id;
    if (list.find(l=>l.id===lid||l.language_id===lid)) return;
    setBusy(b=>({...b,[lid]:true}));
    const res = await post("add-new-language", { resource_slug: resourceSlug, language_id: lid });
    if (res && Number(res.status)===1) setList(l=>[...l, {...lang, relation_id: res.relation_id||Date.now()}]);
    setBusy(b=>({...b,[lid]:false}));
  };

  const remove = async (lang) => {
    const lid = lang.language_id || lang.id;
    setBusy(b=>({...b,[lid]:"rm"}));
    await post("remove-language", { resource_slug: resourceSlug, language_id: lid });
    setList(l=>l.filter(x=>(x.language_id||x.id)!==lid));
    setBusy(b=>({...b,[lid]:false}));
  };

  return (
    <Modal title="Edit Languages" icon="fa-language" iconBg="var(--fc-green-light)" iconColor="var(--fc-green)" onClose={()=>{onRefresh();onClose();}}
      footer={<button onClick={()=>{onRefresh();onClose();}} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill" style={{width:"100%"}}>Done</button>}>
      <label style={LBL}>Current Languages</label>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14, minHeight:32 }}>
        {list.length===0 && <span style={{fontSize:12,color:"var(--fc-text-subtle)"}}>No languages yet</span>}
        {list.map(l=>(
          <span key={l.relation_id} style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 10px", background:"var(--fc-green-light)", border:"1px solid var(--fc-green-border)", borderRadius:99, fontSize:12, fontWeight:600, color:"var(--fc-green-text)" }}>
            {l.value||l.language_name}
            <button onClick={()=>remove(l)} disabled={busy[l.language_id||l.id]==="rm"}
              style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-text-subtle)", padding:0, fontSize:10 }}>
              {busy[l.language_id||l.id]==="rm"?<i className="fa-solid fa-spinner fa-spin"/>:<i className="fa-solid fa-xmark"/>}
            </button>
          </span>
        ))}
      </div>
      <label style={LBL}>Search & Add Languages</label>
      <div style={{ position:"relative", marginBottom:8 }}>
        <input value={search} onChange={e=>{setSearch(e.target.value);doSearch(e.target.value);}} placeholder="Type to search languages..."
          style={INP} onFocus={e=>e.target.style.borderColor="#2563EB"} onBlur={e=>e.target.style.borderColor="var(--fc-border)"} />
        {searching && <i className="fa-solid fa-spinner fa-spin" style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",color:"var(--fc-text-subtle)",fontSize:11}} />}
      </div>
      {results.length>0 && (
        <div style={{ border:"1px solid var(--fc-border)", borderRadius:10, overflow:"hidden", maxHeight:200, overflowY:"auto" }}>
          {results.map((l,i)=>{
            const lid = l.id||l.language_id;
            const already = !!list.find(x=>(x.id||x.language_id)===lid);
            return (
              <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"9px 14px", borderBottom:"1px solid var(--fc-bg-light)", background:already?"var(--fc-bg-light)":"var(--fc-card-bg)", cursor:already?"default":"pointer" }}
                onMouseDown={e=>{e.preventDefault(); if(!already) add(l);}}>
                <span style={{fontSize:13,color:already?"var(--fc-text-subtle)":"var(--fc-text-secondary)",fontWeight:already?400:500}}>{l.value||l.language_name}</span>
                <span style={{ padding:"3px 10px", borderRadius:7, background:already?"var(--fc-border)":"var(--fc-green-light)", color:already?"var(--fc-text-subtle)":"var(--fc-green-text)", fontSize:11, fontWeight:700, pointerEvents:"none" }}>
                  {busy[lid]===true ? "Adding..." : already ? "Added ✓" : "+ Add"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ── Availability modal ────────────────────────────────────────────────────────
function AvailModal({ current, resourceSlug, userSlug, onClose, onRefresh }) {
  const [val,   setVal]   = useState(parseInt(current)||0);
  const [saving,setSaving]= useState(false);
  const [error, setError] = useState("");

  const color = val>=30?"var(--fc-green)":val>=10?"#F59E0B":"var(--fc-red)";
  const label = val>=30?"Available":val>=10?"Partially Available":"Busy";

  const save = async () => {
    setSaving(true); setError("");
    const res = await post("save-resource-availability", { current_user_slug: userSlug, resource_slug: resourceSlug, availability: val });
    setSaving(false);
    if (res && Number(res.status) === 1) { onRefresh(); onClose(); }
    else setError(res?.message || "Failed to save.");
  };

  return (
    <Modal title="Edit Availability" icon="fa-clock" iconBg="var(--fc-primary-light)" iconColor="#2563EB" onClose={onClose}
      footer={
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={onClose} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill">Cancel</button>
          <button onClick={save} disabled={saving} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill" style={{minWidth:80}}>
            {saving ? <><i className="fa-solid fa-spinner fa-spin me-1" style={{fontSize:10}}/>Saving...</> : "Save"}
          </button>
        </div>
      }>
      {/* Big number */}
      <div style={{ textAlign:"center", marginBottom:20 }}>
        <div style={{ fontSize:56, fontWeight:900, color, lineHeight:1 }}>{val}</div>
        <div style={{ fontSize:14, fontWeight:600, color, marginTop:6 }}>hrs/week — {label}</div>
      </div>

      {/* Slider */}
      <input type="range" min="0" max="40" value={val} onChange={e=>setVal(parseInt(e.target.value))}
        style={{ width:"100%", accentColor:color }} />
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"var(--fc-text-subtle)", marginTop:4 }}>
        <span>0 — Busy</span><span>20 hrs</span><span>40 — Available</span>
      </div>

      {/* Quick chips */}
      <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:18, flexWrap:"wrap" }}>
        {[0,10,20,30,40].map(v => (
          <button key={v} onClick={() => setVal(v)}
            style={{ padding:"5px 14px", borderRadius:99, border:`1.5px solid ${val===v?color:"var(--fc-border)"}`, background:val===v?color+"18":"var(--fc-card-bg)", color:val===v?color:"var(--fc-text-muted)", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}>
            {v} hrs
          </button>
        ))}
      </div>

      {error && <div style={{ marginTop:12, padding:"8px 12px", background:"var(--fc-red-light)", border:"1px solid var(--fc-red-border)", borderRadius:8, fontSize:12, color:"var(--fc-red)" }}>{error}</div>}
    </Modal>
  );
}

// ── Location & Phone modal ──────────────────────────────────────────────────
function LocationPhoneModal({ cd, resourceSlug, userSlug, onClose, onRefresh }) {
  const isSet = (v) => v && v !== "Not available";

  const [phone,     setPhone]     = useState(isSet(cd.mobile) ? cd.mobile : "");
  const [countryId, setCountryId] = useState(cd.user_address_country_id || "");
  const [stateId,   setStateId]   = useState(cd.user_address_provience_id || "");
  const [cityId,    setCityId]    = useState(cd.user_address_city_id || "");

  const [countries, setCountries] = useState([]);
  const [states,    setStates]    = useState([]);
  const [cities,    setCities]    = useState([]);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");

  useEffect(() => {
    post("get-all-country", {}).then(res => { if (res?.status === 1) setCountries(res.list || []); });
  }, []);

  // Keep the initial state pre-selected on first load (it already belongs to
  // this country); only reset state/city when the user actively picks a
  // different country afterward.
  const firstLoad = useRef(true);
  useEffect(() => {
    if (!countryId) { setStates([]); return; }
    post("get-all-state", { country_id: countryId }).then(res => {
      setStates(res?.status === 1 ? res.list || [] : []);
    });
    if (!firstLoad.current) { setStateId(""); setCityId(""); }
    firstLoad.current = false;
  }, [countryId]);

  const firstStateLoad = useRef(true);
  useEffect(() => {
    if (!stateId) { setCities([]); return; }
    post("get-all-city", { state_id: stateId }).then(res => {
      setCities(res?.status === 1 ? res.list || [] : []);
    });
    if (!firstStateLoad.current) setCityId("");
    firstStateLoad.current = false;
  }, [stateId]);

  const selectStyle = { ...INP, appearance: "auto" };

  const save = async () => {
    setSaving(true); setError("");
    const res = await post("update-profile-contact-info", {
      current_user_slug: userSlug,
      user_slug:          resourceSlug,
      phone:               phone,
      office_phone:        isSet(cd.office_phone) ? cd.office_phone : "",
      user_street_address: isSet(cd.user_street_address) ? cd.user_street_address : "",
      user_address_city_id:      cityId,
      user_address_provience_id: stateId,
      user_address_country_id:   countryId,
    });
    setSaving(false);
    if (res?.status === 1) { onClose(); onRefresh(); }
    else setError(res?.message || "Unable to save. Please try again.");
  };

  return (
    <Modal title="Edit Location & Phone" icon="fa-location-dot" iconBg="var(--fc-primary-light)" iconColor="#2563EB" onClose={onClose}
      footer={
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={onClose} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill">Cancel</button>
          <button onClick={save} disabled={saving} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
            {saving?"Saving...":"Save Changes"}
          </button>
        </div>
      }>
      {error && <div style={{padding:"10px 12px",background:"var(--fc-red-light)",border:"1px solid var(--fc-red-border)",borderRadius:9,marginBottom:14,fontSize:13,color:"#DC2626"}}>{error}</div>}
      <div style={{marginBottom:12}}>
        <label style={LBL}>Phone</label>
        {inp(phone, setPhone, "e.g. +1 555 123 4567")}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr",gap:12}}>
        <div>
          <label style={LBL}>Country</label>
          <select value={countryId} onChange={e=>setCountryId(e.target.value)} style={selectStyle}>
            <option value="">Select country...</option>
            {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label style={LBL}>State / Province</label>
          <select value={stateId} onChange={e=>setStateId(e.target.value)} disabled={!countryId} style={selectStyle}>
            <option value="">{countryId ? "Select state..." : "Select a country first"}</option>
            {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label style={LBL}>City</label>
          <select value={cityId} onChange={e=>setCityId(e.target.value)} disabled={!stateId} style={selectStyle}>
            <option value="">{stateId ? "Select city..." : "Select a state first"}</option>
            {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>
    </Modal>
  );
}

// ── Bio editor ────────────────────────────────────────────────────────────────
function BioEditor({ bio, resourceSlug, userSlug }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg,  setMsg]  = useState("");

  // Sync bio prop → state when it loads (rd may not be ready on first render)
  useEffect(() => { if (bio) setText(bio); }, [bio]);

  const save = async () => {
    if (!text.trim()) { setMsg("Please enter a summary."); return; }
    setBusy(true); setMsg("");
    const res = await post("update-summary-details", {
      current_user_slug: userSlug,
      resource_slug:     resourceSlug,
      profile_bio:       text,
    });
    setBusy(false);
    if (res && Number(res.status) === 1) {
      setMsg("✓ Saved successfully!");
    } else {
      setMsg(res?.message || "Failed to save. Please try again.");
    }
    setTimeout(() => setMsg(""), 3000);
  };

  return (
    <div>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={6}
        placeholder="Write a professional summary..." className="form-control"
        style={{ fontSize:14, lineHeight:1.7, resize:"vertical", background:"var(--fc-card-bg)", color:"var(--fc-text-primary)", borderColor:"var(--fc-border)" }} />
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:10 }}>
        <span style={{ fontSize:12, color:msg.startsWith("✓") ? "var(--fc-green)" : msg ? "var(--fc-red)" : "transparent" }}>{msg||"."}</span>
        <button onClick={save} disabled={busy} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
          {busy
            ? <><i className="fa-solid fa-spinner fa-spin me-1" style={{fontSize:10}}/>Saving...</>
            : <><i className="fa-solid fa-floppy-disk me-1" style={{fontSize:10}}/>Save Summary</>}
        </button>
      </div>
    </div>
  );
}

// ── Education CRUD ────────────────────────────────────────────────────────────
function EduEditor({ items, resourceSlug, userSlug, onRefresh }) {
  const empty = { degree_name:"", subject:"", school_name:"", passed_on:"" };
  const [editId,   setEditId]   = useState(null);  // education_history_id being edited
  const [form,     setForm]     = useState(empty);
  const [saving,   setSaving]   = useState(false);
  const [showAdd,  setShowAdd]  = useState(false);
  const [removing, setRemoving] = useState({});
  const [err,      setErr]      = useState("");
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = async () => {
    if (!form.degree_name.trim()) { setErr("Degree name is required"); return; }
    setSaving(true); setErr("");
    let res;
    if (editId) {
      // update: uses education_id (not education_history_id!)
      res = await post("update-education-details", {
        current_user_slug: userSlug,
        resource_slug:     resourceSlug,
        education_id:      editId,
        degree_name:       form.degree_name,
        subject:           form.subject,
        school_name:       form.school_name,
        passed_on:         form.passed_on,
      });
    } else {
      res = await post("save-education-details", {
        current_user_slug: userSlug,
        resource_slug:     resourceSlug,
        degree_name:       form.degree_name,
        subject:           form.subject,
        school_name:       form.school_name,
        passed_on:         form.passed_on,
      });
    }
    setSaving(false);
    if (res && Number(res.status) === 1) {
      setShowAdd(false); setEditId(null); setForm(empty);
      onRefresh();
    } else {
      setErr(res?.message || "Failed to save. Please try again.");
    }
  };

  const remove = async (id) => {
    setRemoving(r=>({...r,[id]:true}));
    await post("remove-education-details", {
      resource_slug:       resourceSlug,
      education_history_id: id,
    });
    setRemoving(r=>({...r,[id]:false}));
    onRefresh();
  };

  const startEdit = (e) => {
    setEditId(e.education_history_id);
    setForm({
      degree_name: e.degree_name || "",
      subject:     e.subject     || "",
      school_name: e.school_name || "",
      passed_on:   toInputDate(e.passed_on),
    });
    setShowAdd(true);
    setErr("");
  };

  return (
    <div>
      {/* Add button at top */}
      {!showAdd && (
        <div style={{ marginBottom:12 }}>
          <button onClick={()=>{setShowAdd(true);setEditId(null);setForm(empty);}}
            className="fc-btn fc-btn-sm fc-btn-secondary fc-btn-pill">
            <i className="fa-solid fa-plus me-1" style={{fontSize:10}} />Add Education
          </button>
        </div>
      )}

      {/* Add/Edit form — shown at top when active */}
      {showAdd && (
        <div style={{ background:"var(--fc-green-light)", border:"1px solid var(--fc-green-border)", borderRadius:12, padding:16, marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"var(--fc-green-text)", marginBottom:12 }}>
            {editId ? "Edit Education" : "Add Education"}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
            <div><label style={LBL}>Degree Name *</label>{inp(form.degree_name, v=>set("degree_name",v), "e.g. Bachelor of Science")}</div>
            <div><label style={LBL}>Subject / Major</label>{inp(form.subject, v=>set("subject",v), "e.g. Computer Science")}</div>
            <div><label style={LBL}>School / University</label>{inp(form.school_name, v=>set("school_name",v), "Institution name")}</div>
            <div><label style={LBL}>Graduation Date</label>{inp(form.passed_on, v=>set("passed_on",v), "", "date")}</div>
          </div>
          {err && <div style={{ fontSize:12, color:"var(--fc-red)", marginBottom:8 }}>{err}</div>}
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={save} disabled={saving} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
              {saving ? <><i className="fa-solid fa-spinner fa-spin me-1" style={{fontSize:9}}/>Saving...</> : "Save"}
            </button>
            <button onClick={()=>{setShowAdd(false);setEditId(null);setForm(empty);setErr("");}}
              className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill">Cancel</button>
          </div>
        </div>
      )}

      {items.map((e,i) => (
        <div key={e.education_history_id||i} style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"12px 14px", background:editId===e.education_history_id?"var(--fc-primary-light)":"var(--fc-bg-light)", border:"1px solid var(--fc-border)", borderRadius:10, marginBottom:8, transition:"background 0.15s" }}>
          <div style={{ width:36, height:36, borderRadius:9, background:"var(--fc-primary-light)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <i className="fa-solid fa-graduation-cap" style={{fontSize:14,color:"#2563EB"}} />
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)" }}>{e.degree_name||"—"}{e.subject ? ` in ${e.subject}` : ""}</div>
            <div style={{ fontSize:12, color:"var(--fc-text-muted)", fontWeight:600 }}>{e.school_name}</div>
            {e.passed_on && <div style={{ fontSize:11, color:"var(--fc-text-subtle)", marginTop:2 }}>{e.passed_on}</div>}
          </div>
          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
            <button onClick={()=>startEdit(e)} style={{ width:28, height:28, borderRadius:7, background:"var(--fc-primary-light)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <i className="fa-solid fa-pen" style={{fontSize:11,color:"#2563EB"}} />
            </button>
            <button onClick={()=>remove(e.education_history_id)} disabled={removing[e.education_history_id]}
              style={{ width:28, height:28, borderRadius:7, background:"var(--fc-red-light)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              {removing[e.education_history_id]
                ? <i className="fa-solid fa-spinner fa-spin" style={{fontSize:10,color:"var(--fc-red)"}}/>
                : <i className="fa-solid fa-trash" style={{fontSize:11,color:"var(--fc-red)"}} />}
            </button>
          </div>
        </div>
      ))}

    </div>
  );
}

// ── Experience CRUD ───────────────────────────────────────────────────────────
function ExpEditor({ items, resourceSlug, userSlug, onRefresh }) {
  const empty = { employer_name:"", job_title:"", start_date:"", end_date:"", is_current:0, description:"" };
  const [editItem, setEditItem] = useState(null);
  const [form,     setForm]     = useState(empty);
  const [saving,   setSaving]   = useState(false);
  const [showAdd,  setShowAdd]  = useState(false);
  const [removing, setRemoving] = useState({});
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = async () => {
    setSaving(true);
    const params = { current_user_slug: userSlug, resource_slug: resourceSlug, ...form };
    if (editItem) {
      await post("update-employment-details", { ...params, employment_id: editItem });
    } else {
      await post("save-employment-details", params);
    }
    setSaving(false); setShowAdd(false); setEditItem(null); setForm(empty); onRefresh();
  };

  const remove = async (id) => {
    setRemoving(r=>({...r,[id]:true}));
    await post("remove-employment-details", { current_user_slug: userSlug, resource_slug: resourceSlug, employment_history_id: id });
    setRemoving(r=>({...r,[id]:false})); onRefresh();
  };

  const startEdit = (e) => {
    setEditItem(e.employment_history_id || e.employment_id);
    setForm({ employer_name:e.employer_name||"", job_title:e.job_title||"", start_date:toInputDate(e.start_date), end_date:toInputDate(e.end_date), is_current:parseInt(e.is_current)===1?1:0, description:e.description||"" });
    setShowAdd(true);
  };

  return (
    <div>
      {/* Add button at top */}
      {!showAdd && (
        <div style={{ marginBottom:12 }}>
          <button onClick={()=>{setShowAdd(true);setEditItem(null);setForm(empty);}}
            className="fc-btn fc-btn-sm fc-btn-secondary fc-btn-pill">
            <i className="fa-solid fa-plus me-1" style={{fontSize:10}} />Add Experience
          </button>
        </div>
      )}

      {/* Form at top */}
      {showAdd && (
        <div style={{ background:"var(--fc-green-light)", border:"1px solid var(--fc-green-border)", borderRadius:12, padding:16, marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"var(--fc-green-text)", marginBottom:12 }}>{editItem?"Edit":"Add"} Experience</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
            <div><label style={LBL}>Employer Name</label>{inp(form.employer_name,v=>set("employer_name",v),"Company name")}</div>
            <div><label style={LBL}>Job Title</label>{inp(form.job_title,v=>set("job_title",v),"Your role")}</div>
            <div><label style={LBL}>Start Date</label>{inp(form.start_date,v=>set("start_date",v),"","date")}</div>
            <div>
              <label style={LBL}>End Date</label>
              {!form.is_current && inp(form.end_date,v=>set("end_date",v),"","date")}
              <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--fc-text-muted)", marginTop:6, cursor:"pointer" }}>
                <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}><input type="checkbox" checked={parseInt(form.is_current)===1} onChange={e=>set("is_current",e.target.checked?1:0)} style={{accentColor:"#2563EB"}} />Currently working here</label>
              </label>
            </div>
          </div>
          <div style={{ marginBottom:10 }}>
            <label style={LBL}>Description</label>
            <textarea value={form.description} onChange={e=>set("description",e.target.value)} rows={3} placeholder="Describe your role..."
              style={{...INP,resize:"vertical"}} onFocus={e=>e.target.style.borderColor="#2563EB"} onBlur={e=>e.target.style.borderColor="var(--fc-border)"} />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={save} disabled={saving} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
              {saving?"Saving...":"Save"}
            </button>
            <button onClick={()=>{setShowAdd(false);setEditItem(null);setForm(empty);}} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill">Cancel</button>
          </div>
        </div>
      )}

      {items.map((e,i)=>(
        <div key={e.employment_history_id||i} style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"12px 14px", background:"var(--fc-bg-light)", border:"1px solid var(--fc-border)", borderRadius:10, marginBottom:8 }}>
          <div style={{ width:36, height:36, borderRadius:9, background:"var(--fc-green-light)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <i className="fa-solid fa-briefcase" style={{fontSize:14,color:"var(--fc-green)"}} />
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)" }}>{e.job_title}</div>
            <div style={{ fontSize:12, color:"var(--fc-text-muted)", fontWeight:600 }}>{e.employer_name}</div>
            <div style={{ fontSize:11, color:"var(--fc-text-subtle)" }}>
              {e.start_date}{e.start_date && " — "}{parseInt(e.is_current)===1 ? "Present" : e.end_date||""}
            </div>
            {e.description && <p style={{ fontSize:12, color:"var(--fc-text-muted)", marginTop:4, lineHeight:1.5, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>{e.description}</p>}
          </div>
          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
            <button onClick={()=>startEdit(e)} style={{ width:28, height:28, borderRadius:7, background:"var(--fc-green-light)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <i className="fa-solid fa-pen" style={{fontSize:11,color:"var(--fc-green)"}} />
            </button>
            <button onClick={()=>remove(e.employment_history_id)} disabled={removing[e.employment_history_id]} style={{ width:28, height:28, borderRadius:7, background:"var(--fc-red-light)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              {removing[e.employment_history_id]?<i className="fa-solid fa-spinner fa-spin" style={{fontSize:10,color:"var(--fc-red)"}}/>:<i className="fa-solid fa-trash" style={{fontSize:11,color:"var(--fc-red)"}} />}
            </button>
          </div>
        </div>
      ))}


    </div>
  );
}

// ── TABS (same classes as view page) ─────────────────────────────────────────
const TABS = [
  { key:"summary",    label:"Summary",    icon:"fa-user"           },
  { key:"education",  label:"Education",  icon:"fa-graduation-cap" },
  { key:"experience", label:"Experience", icon:"fa-briefcase"      },
];

// ── Main page ─────────────────────────────────────────────────────────────────
export default function EditProfilePage() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { user, userSlug } = useAuth();
  const { isFirmOwner, isRM } = useRole();
  const resourceSlug = id || userSlug;
  const fileRef      = useRef();

  const [rd,        setRd]        = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState("summary");
  const [avatarSrc, setAvatarSrc] = useState(null);
  const [avatarBusy,setAvatarBusy]= useState(false);
  const [modal,     setModal]     = useState(null); // "skills"|"certs"|"langs"|"avail"|"name"|"location"
  const [toast,     setToast]     = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Name/role edit
  const [fName, setFName] = useState("");
  const [lName, setLName] = useState("");
  const [role,  setRole]  = useState("");
  const [saving,setSaving]= useState(false);

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  const handleDeleteProfile = async () => {
    setDeleteBusy(true);
    const res = await post("remove-account", { user_slug: userSlug, resource_slug: resourceSlug });
    setDeleteBusy(false);
    setShowDeleteConfirm(false);
    if (res?.status === 1) {
      cache.clear(`resources_${userSlug}`);
      navigate("/resources");
    } else {
      showToast(res?.message || "Unable to delete this profile. Please try again.", "error");
    }
  };

  const load = useCallback(() => {
    if (!userSlug || !resourceSlug) return;
    post("get-resource-details", { resource_slug: resourceSlug, user_slug: userSlug }).then(data => {
      if (data?.status===1 && data?.resource_details) {
        const rd = data.resource_details;
        setRd(rd);
        const pd = rd.profile_details||{};
        // The backend only ever returns a combined `profile_name`
        // (CONCAT(first_name, " ", last_name)) - it never sends separate
        // first_name/last_name fields, so those always came back empty.
        // Fall back to splitting profile_name when the split fields aren't present.
        let firstNameVal = rd.first_name || pd.first_name;
        let lastNameVal  = rd.last_name  || pd.last_name;
        if (!firstNameVal && !lastNameVal) {
          const combined = (rd.profile_name || pd.profile_name || "").trim();
          if (combined) {
            const parts = combined.split(/\s+/);
            firstNameVal = parts[0] || "";
            lastNameVal  = parts.slice(1).join(" ") || "";
          }
        }
        setFName(firstNameVal || "");
        setLName(lastNameVal || "");
        setRole(rd.user_profile_role || pd.user_profile_role || "");
        if (pd.profile_image_path) setAvatarSrc(PROFILE_IMG_BASE+pd.profile_image_path);
      }
      setLoading(false);
    });
  }, [userSlug, resourceSlug]);

  useEffect(() => { load(); }, [load]);

  const saveNameRole = async () => {
    setSaving(true);
    const res = await post("update-profile-basic-info-v2", {
      current_user_slug: userSlug,
      user_slug:         resourceSlug,
      first_name:        fName,
      last_name:         lName,
      user_profile_role: role,
    });
    setSaving(false);
    if (res?.status===1) { showToast("Saved!"); setModal(null); load(); }
    else showToast(res?.message||"Failed.", "error");
  };

  const handleAvatar = async (file) => {
    if (!file) return;
    setAvatarSrc(URL.createObjectURL(file));
    setAvatarBusy(true);
    const fd = new FormData();
    fd.append("user_slug", resourceSlug);
    fd.append("rm_slug",   userSlug);
    fd.append("file",      file);
    const res = await axios.post(`${API_BASE}user-avatar-upload`, fd).then(r=>r.data).catch(()=>null);
    setAvatarBusy(false);
    res?.status===1 ? showToast("Photo updated!") : showToast("Photo upload failed.", "error");
  };

  if (loading) return (
    <div className="d-flex flex-column gap-3">
      {[1,2,3].map(i=><div key={i} style={{height:80,background:"var(--fc-card-bg)",border:"1px solid var(--fc-border)",borderRadius:14}} />)}
    </div>
  );

  if (!rd) return (
    <SectionCard>
      <div className="text-center py-4">
        <p className="fw-bold mb-2">Profile not found.</p>
        <button className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill" onClick={()=>navigate(-1)}>Go back</button>
      </div>
    </SectionCard>
  );

  // Ownership check - this was previously missing entirely, meaning any
  // authenticated user hitting /resources/edit-resource/:id or
  // /my-team/edit-resource/:id with any target id could edit that profile,
  // regardless of whether they actually manage that resource. /edit-profile
  // itself (no :id) is always safe since resourceSlug falls back to your
  // own userSlug above - this only matters for the :id-based routes.
  const isEditingSelf = String(resourceSlug) === String(userSlug);
  const targetFirmId   = rd.firm_id || rd.profile_details?.firm_id;
  const targetRMId     = rd.resource_manager_id || rd.profile_details?.resource_manager_id;
  const canEdit = isEditingSelf || isFirmOwner || (isRM &&
    (String(targetRMId) === String(user?.user_id) ||
     String(targetFirmId) === String(user?.firm_details?.firm_id) ||
     String(rd?.rm_details?.resource_manager_id) === String(user?.user_id)));

  if (!canEdit) return (
    <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, padding:"48px 24px", textAlign:"center" }}>
      <i className="fa-solid fa-lock" style={{ fontSize:28, color:"var(--fc-text-hint)", display:"block", marginBottom:12 }} />
      <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)" }}>Access Denied</div>
      <div style={{ fontSize:13, color:"var(--fc-text-muted)", marginTop:4 }}>You don't have permission to edit this profile.</div>
    </div>
  );

  const pd    = rd.profile_details    || {};
  const cd    = rd.contact_details    || {};
  const skills    = rd.skill_details || [];
  const skillCount = parseInt(rd.skill_count?.skill_count) || skills.length;
  const certs  = rd.certification_details || [];
  const langs  = rd.language_details_V2 || rd.language_details || [];
  const edu = [...(rd.education_details||[])].sort((a,b) => parseDate(b.passed_on) - parseDate(a.passed_on));
  const emp = [...(rd.employment_details||[])].sort((a,b) => {
    // Current jobs first
    const aCurrent = parseInt(a.is_current)===1;
    const bCurrent = parseInt(b.is_current)===1;
    if (aCurrent && !bCurrent) return -1;
    if (!aCurrent && bCurrent) return 1;
    // Then by start_date descending
    return parseDate(b.start_date) - parseDate(a.start_date);
  });
  const avail  = parseInt(rd.availability_details?.availability ?? pd.availability) || 0;
  const availColor = avail>=30?"var(--fc-green)":avail>=10?"#F59E0B":"var(--fc-red)";
  const availLabel = avail>=30?"Available":avail>=10?"Partial":"Busy";
  const name = `${fName} ${lName}`.trim();
  const initials = name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()||"?";

  return (
    <div>
      {/* ── Profile header (same as view page) ── */}
      <div className="fc-profile-header">
        <div className="fc-profile-top">
          {/* Identity */}
          <div className="fc-profile-identity flex-grow-1">
            {/* Avatar */}
            <div className="position-relative flex-shrink-0" style={{cursor:"pointer"}} onClick={()=>!avatarBusy&&fileRef.current?.click()} title="Click to change photo">
              <div className="fc-profile-avatar" style={{background:"#2563EB18",border:"2px solid #2563EB30",color:"#2563EB",overflow:"hidden"}}>
                {avatarSrc ? <img src={avatarSrc} alt={name} style={{width:"100%",height:"100%",objectFit:"cover"}} /> : initials}
                <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",opacity:0,transition:"opacity 0.15s",borderRadius:"inherit"}}
                  onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0}>
                  {avatarBusy ? <i className="fa-solid fa-spinner fa-spin" style={{color:"#fff",fontSize:18}} /> : <i className="fa-solid fa-camera" style={{color:"#fff",fontSize:18}} />}
                </div>
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}}
                onChange={e=>{const f=e.target.files[0];if(f)handleAvatar(f);}} />
            </div>

            {/* Name / role */}
            <div className="overflow-hidden">
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <h1 className="fc-profile-name">{name||"No name set"}</h1>
                <span className="badge rounded-pill" style={{fontSize:10,background:"var(--fc-primary-light)",color:"#2563EB"}}>Editing</span>
                <button onClick={()=>setModal("name")} style={{background:"none",border:"none",cursor:"pointer",color:"#2563EB",fontSize:13,padding:"2px 6px"}} title="Edit name & role">
                  <i className="fa-solid fa-pen" />
                </button>
              </div>
              <p className="fc-profile-role mb-1">{role||"No role set"}</p>
              <div className="fc-profile-meta d-flex align-items-center gap-2 flex-wrap mb-1">
                {cd.user_email && cd.user_email!=="Not available" && (
                  <span className="fc-profile-meta-item fc-profile-meta-email">
                    <i className="fa-solid fa-envelope fc-profile-meta-icon" style={{ color: "var(--fc-blue)" }} />{cd.user_email}
                  </span>
                )}
                {cd.mobile && cd.mobile!=="Not available" && (
                  <span className="fc-profile-meta-item fc-profile-meta-email">
                    <i className="fa-solid fa-phone fc-profile-meta-icon" style={{ color: "var(--fc-green)" }} />{cd.mobile}
                  </span>
                )}
                {cd.city_name ? (
                  <span className="fc-profile-meta-item d-flex align-items-center gap-1">
                    <i className="fa-solid fa-location-dot fc-profile-meta-icon" />{[cd.city_name,cd.state_name,cd.country_name].filter(Boolean).join(", ")}
                    <button onClick={()=>setModal("location")} style={{background:"none",border:"none",cursor:"pointer",color:"#2563EB",fontSize:12,padding:"2px 6px"}} title="Edit location & phone">
                      <i className="fa-solid fa-pen" />
                    </button>
                  </span>
                ) : (
                  <button onClick={()=>setModal("location")} className="fc-btn fc-btn-xs fc-btn-ghost fc-btn-pill">
                    <i className="fa-solid fa-location-dot" style={{marginRight:5}} />Add location & phone
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* View profile btn */}
          <div className="d-flex gap-2 flex-shrink-0">
            <button onClick={()=>navigate(`/resources/details/${resourceSlug}`)} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill">
              <i className="fa-solid fa-eye me-1" style={{fontSize:11}} />View Profile
            </button>
            {(isFirmOwner || isRM) && !isEditingSelf && (
              <button onClick={()=>setShowDeleteConfirm(true)} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill" style={{ border:"1px solid var(--fc-red-border)", color:"var(--fc-red)" }}>
                <i className="fa-solid fa-trash-can me-1" style={{fontSize:11}} />Delete Profile
              </button>
            )}
          </div>
        </div>

      </div>

      {/* ── 4 glance cards ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12,margin:"14px 0"}}>

        {/* Skills card */}
        <div style={{background:"var(--fc-card-bg)",border:"1px solid var(--fc-border)",borderRadius:14,padding:"16px 18px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              <div style={{width:28,height:28,borderRadius:7,background:"var(--fc-primary-light)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <i className="fa-solid fa-code" style={{fontSize:12,color:"#2563EB"}}/>
              </div>
              <span style={{fontSize:12,fontWeight:700,color:"var(--fc-text-secondary)"}}>Skills</span>
              <span style={{fontSize:11,fontWeight:700,padding:"1px 7px",background:"var(--fc-primary-light)",color:"#2563EB",borderRadius:99}}>{skillCount}</span>
            </div>
            <button onClick={()=>setModal("skills")} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill" style={{padding:"4px 10px",fontSize:11}}>
              <i className="fa-solid fa-pen me-1" style={{fontSize:9}}/>Edit
            </button>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
            {skills.slice(0,6).map(s=><span key={s.skill_id} style={{padding:"3px 9px",background:"var(--fc-primary-light)",border:"1px solid var(--fc-primary-border)",borderRadius:99,fontSize:11,fontWeight:600,color:"#2563EB"}}>{s.skill_name}</span>)}
            {skillCount>6 && <span style={{padding:"3px 9px",background:"var(--fc-border-light)",borderRadius:99,fontSize:11,color:"var(--fc-text-muted)"}}>+{skillCount-6} more</span>}
            {skillCount===0 && <button onClick={()=>setModal("skills")} style={{fontSize:12,color:"#2563EB",background:"none",border:"1px dashed var(--fc-primary-border)",borderRadius:7,padding:"3px 10px",cursor:"pointer",fontFamily:"inherit"}}>+ Add skills</button>}
          </div>
        </div>

        {/* Certs card */}
        <div style={{background:"var(--fc-card-bg)",border:"1px solid var(--fc-border)",borderRadius:14,padding:"16px 18px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              <div style={{width:28,height:28,borderRadius:7,background:"var(--fc-amber-light)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <i className="fa-solid fa-certificate" style={{fontSize:12,color:"#D97706"}}/>
              </div>
              <span style={{fontSize:12,fontWeight:700,color:"var(--fc-text-secondary)"}}>Certifications</span>
              <span style={{fontSize:11,fontWeight:700,padding:"1px 7px",background:"var(--fc-amber-light)",color:"#D97706",borderRadius:99}}>{certs.length}</span>
            </div>
            <button onClick={()=>setModal("certs")} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill" style={{padding:"4px 10px",fontSize:11}}>
              <i className="fa-solid fa-pen me-1" style={{fontSize:9}}/>Edit
            </button>
          </div>
          {certs.length===0
            ? <button onClick={()=>setModal("certs")} style={{fontSize:12,color:"#D97706",background:"none",border:"1px dashed var(--fc-amber-border)",borderRadius:7,padding:"3px 10px",cursor:"pointer",fontFamily:"inherit"}}>+ Add certification</button>
            : certs.slice(0,4).map((c,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 0",borderBottom:i<Math.min(certs.length,4)-1?"1px solid var(--fc-bg-light)":"none"}}>
                <i className="fa-solid fa-circle-check" style={{color:"#D97706",fontSize:10,flexShrink:0}}/>
                <span style={{fontSize:12,color:"var(--fc-text-secondary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.certification_title}</span>
              </div>
            ))
          }
          {certs.length>4 && <div style={{fontSize:11,color:"var(--fc-text-subtle)",marginTop:4}}>+{certs.length-4} more</div>}
        </div>

        {/* Languages card */}
        <div style={{background:"var(--fc-card-bg)",border:"1px solid var(--fc-border)",borderRadius:14,padding:"16px 18px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              <div style={{width:28,height:28,borderRadius:7,background:"var(--fc-green-light)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <i className="fa-solid fa-language" style={{fontSize:12,color:"var(--fc-green)"}}/>
              </div>
              <span style={{fontSize:12,fontWeight:700,color:"var(--fc-text-secondary)"}}>Languages</span>
              <span style={{fontSize:11,fontWeight:700,padding:"1px 7px",background:"var(--fc-green-light)",color:"var(--fc-green)",borderRadius:99}}>{langs.length}</span>
            </div>
            <button onClick={()=>setModal("langs")} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill" style={{padding:"4px 10px",fontSize:11}}>
              <i className="fa-solid fa-pen me-1" style={{fontSize:9}}/>Edit
            </button>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {langs.length===0
              ? <button onClick={()=>setModal("langs")} style={{fontSize:12,color:"var(--fc-green)",background:"none",border:"1px dashed var(--fc-green-border)",borderRadius:7,padding:"3px 10px",cursor:"pointer",fontFamily:"inherit"}}>+ Add language</button>
              : langs.map((l,i)=><span key={i} style={{padding:"4px 10px",background:"var(--fc-green-light)",border:"1px solid var(--fc-green-border)",borderRadius:99,fontSize:12,fontWeight:600,color:"var(--fc-green-text)"}}>{l.value||l.language_name}</span>)}
          </div>
        </div>

        {/* Availability card */}
        <div style={{background:"var(--fc-card-bg)",border:"1px solid var(--fc-border)",borderRadius:14,padding:"16px 18px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              <div style={{width:28,height:28,borderRadius:7,background:`${availColor}15`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <i className="fa-solid fa-clock" style={{fontSize:12,color:availColor}}/>
              </div>
              <span style={{fontSize:12,fontWeight:700,color:"var(--fc-text-secondary)"}}>Availability</span>
            </div>
            <button onClick={()=>setModal("avail")} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill" style={{padding:"4px 10px",fontSize:11}}>
              <i className="fa-solid fa-pen me-1" style={{fontSize:9}}/>Edit
            </button>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <div style={{position:"relative",width:56,height:56,flexShrink:0}}>
              <svg width="56" height="56" style={{transform:"rotate(-90deg)"}}>
                <circle cx="28" cy="28" r="22" fill="none" stroke="var(--fc-border-light)" strokeWidth="5"/>
                <circle cx="28" cy="28" r="22" fill="none" stroke={availColor} strokeWidth="5"
                  strokeDasharray={`${Math.min(avail/40,1)*138} 138`} strokeLinecap="round"/>
              </svg>
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:availColor}}>{avail}</div>
            </div>
            <div>
              <div style={{fontSize:16,fontWeight:800,color:availColor}}>{availLabel}</div>
              <div style={{fontSize:12,color:"var(--fc-text-subtle)"}}>{avail} hrs / week</div>
            </div>
          </div>
        </div>

      </div>

      {/* ── Tabs (same classes as view page) ── */}
      <div className="fc-profile-tabs">
        {TABS.map(t=>{
          const isActive = t.key===tab;
          return (
            <button key={t.key} onClick={()=>setTab(t.key)} className={`fc-profile-tab-btn${isActive?" active":""}`}>
              <div className="fc-profile-tab-icon" style={{background:isActive?"rgba(255,255,255,0.15)":"var(--fc-bg-light)"}}>
                <i className={`fa-solid ${t.icon}`} style={{color:isActive?"#fff":"var(--fc-text-subtle)"}} />
              </div>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Tab content ── */}
      {tab==="summary" && (
        <SectionCard title="Bio / Summary">
          <BioEditor bio={rd.profile_bio || pd.profile_bio} resourceSlug={resourceSlug} userSlug={userSlug} />
        </SectionCard>
      )}
      {tab==="education" && (
        <SectionCard title="Education">
          <EduEditor items={edu} resourceSlug={resourceSlug} userSlug={userSlug} onRefresh={load} />
        </SectionCard>
      )}
      {tab==="experience" && (
        <SectionCard title="Work Experience">
          <ExpEditor items={emp} resourceSlug={resourceSlug} userSlug={userSlug} onRefresh={load} />
        </SectionCard>
      )}

      {/* ── Modals ── */}
      {modal==="name" && (
        <Modal title="Edit Name & Role" icon="fa-user-pen" iconBg="var(--fc-primary-light)" iconColor="#2563EB" onClose={()=>setModal(null)}
          footer={
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button onClick={()=>setModal(null)} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill">Cancel</button>
              <button onClick={saveNameRole} disabled={saving} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
                {saving?"Saving...":"Save Changes"}
              </button>
            </div>
          }>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div><label style={LBL}>First Name</label>{inp(fName,setFName,"First name")}</div>
            <div><label style={LBL}>Last Name</label>{inp(lName,setLName,"Last name")}</div>
            <div style={{gridColumn:"1/-1"}}><label style={LBL}>Job Role / Title</label>{inp(role,setRole,"e.g. Senior Developer")}</div>
          </div>
        </Modal>
      )}

      {modal==="location" && (
        <LocationPhoneModal cd={cd} resourceSlug={resourceSlug} userSlug={userSlug}
          onClose={()=>setModal(null)} onRefresh={load} />
      )}

      {modal==="skills" && (
        <SkillsModal skills={skills} resourceSlug={resourceSlug} userSlug={userSlug}
          onClose={()=>setModal(null)} onRefresh={load} />
      )}
      {modal==="certs" && (
        <CertsModal certs={certs} resourceSlug={resourceSlug} userSlug={userSlug}
          onClose={()=>setModal(null)} onRefresh={load} />
      )}
      {modal==="langs" && (
        <LangsModal langs={langs} resourceSlug={resourceSlug} userSlug={userSlug}
          onClose={()=>setModal(null)} onRefresh={load} />
      )}
      {modal==="avail" && (
        <AvailModal current={avail} resourceSlug={resourceSlug} userSlug={userSlug}
          onClose={()=>setModal(null)} onRefresh={load} />
      )}

      {showDeleteConfirm && (
        <SharedModal title="Delete this profile?" onClose={() => setShowDeleteConfirm(false)} maxWidth={400}
          footer={<ModalFooter onCancel={() => setShowDeleteConfirm(false)} onConfirm={handleDeleteProfile}
            confirmLabel="Yes, delete" confirmTone="danger" confirmBusy={deleteBusy} />}>
          <p style={{ fontSize:13, color:"var(--fc-text-secondary)" }}>
            This will permanently remove <strong style={{color:"var(--fc-text-primary)"}}>{name || "this profile"}</strong> and cannot be undone.
          </p>
        </SharedModal>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{position:"fixed",bottom:24,right:24,zIndex:9999,background:toast.type==="error"?"var(--fc-red-light)":"var(--fc-text-primary)",border:toast.type==="error"?"1px solid var(--fc-red-border)":"none",borderRadius:12,padding:"12px 18px",display:"flex",alignItems:"center",gap:10,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
          <div style={{width:22,height:22,borderRadius:6,background:toast.type==="error"?"var(--fc-red)":"var(--fc-green)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <i className={`fa-solid ${toast.type==="error"?"fa-xmark":"fa-check"}`} style={{fontSize:10,color:"#fff"}} />
          </div>
          <span style={{fontSize:13,fontWeight:600,color:toast.type==="error"?"var(--fc-red)":"#fff"}}>{toast.msg}</span>
        </div>
      )}
    </div>
  );
}
